import { Box, useTheme } from "@mui/material";
import { useFormatNumber } from "src/utils/formatNumber";

export interface RecipeValue {
  value: number | null;
  strikeIfAboveValue: number | null;
  strikeIfBelowValue: number | null;
  material: string;
  isInRotation: boolean; // true = ratio, false = addition
  ignored: boolean;
}

interface RatioRecipeProps {
  values: RecipeValue[];
  getColor?: (value: RecipeValue) => string;
}

/**
 * Renders a recipe with ratio formatting
 * Format example: "2.0:1.0:2.0 +3.0" (HMS:SHRED:TURN +RCLM)
 *
 * Colors:
 * - When getColor provided: uses custom color function
 * - Otherwise: uses default text color
 */
export const RatioRecipe = ({ values, getColor }: RatioRecipeProps) => {
  const theme = useTheme();
  const formatNumber = useFormatNumber();

  if (values.length === 0) {
    return <span>-</span>;
  }

  // Separate in-rotation (ratios) from additions
  const inRotation = values.filter((v) => v.isInRotation);
  const additions = values.filter((v) => !v.isInRotation);

  const defaultGetColor = () => theme.palette.text.primary;
  const colorFn = getColor ?? defaultGetColor;

  const allAdditions = values.every(({ isInRotation }) => !isInRotation);
  const allInteger = values.every(
    ({ value }) =>
      value == null || Number.isInteger(Math.round(value * 10) / 10)
  );

  const formatValue = (value: number | null) => {
    if (value == null) {
      return "-";
    }
    return formatNumber(allInteger ? 0 : 1)(value);
  };

  return allAdditions ? (
    <Box
      sx={{
        display: "flex",
        gap: 1.0,
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {additions.map((recipeValue) => {
        const color = colorFn(recipeValue);

        return (
          <Box
            key={`${recipeValue.material}-value`}
            component="span"
            sx={{
              ...theme.typography.body2,
              color,
              fontWeight: "fontWeightSemiBold",
              width: "25px",
              textAlign: "center",
            }}
          >
            {formatValue(recipeValue.value)}
          </Box>
        );
      })}
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        fontWeight: "fontWeightMedium",
      }}
    >
      {/* Render in-rotation values with colons */}
      {inRotation.map((recipeValue, index) => {
        const color = colorFn(recipeValue);

        return (
          <Box
            key={recipeValue.material}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              component="span"
              sx={{
                ...theme.typography.body2,
                color,
                fontWeight: "fontWeightSemiBold",
              }}
            >
              {formatValue(recipeValue.value)}
            </Box>
            {index < inRotation.length - 1 && (
              <Box
                component="span"
                sx={{
                  color: theme.palette.text.primary,
                  opacity: 0.8,
                }}
              >
                :
              </Box>
            )}
          </Box>
        );
      })}

      {/* Render additions with + prefix */}
      {additions.map((recipeValue) => {
        const color = colorFn(recipeValue);

        return (
          <Box
            key={recipeValue.material}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
            }}
          >
            <Box
              component="span"
              sx={{
                color: theme.palette.text.primary,
                opacity: 0.8,
              }}
            >
              +
            </Box>
            <Box
              component="span"
              sx={{
                ...theme.typography.body2,
                color,
                fontWeight: "fontWeightSemiBold",
              }}
            >
              {formatValue(recipeValue.value)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
